import Cookies from 'js-cookie'

import type { User } from '@/types/user'
import { config } from '@/config'
import { operatorKey } from '@/lib/operator'
import { axiosInstance } from '@/providers/rest-data-provider/utils'

export interface SignUpParams {
	firstName: string
	lastName: string
	email: string
	password: string
}

export interface SignInWithOAuthParams {
	provider: 'google' | 'discord'
}

export interface SignInWithPasswordParams {
	email: string
	password: string
}

export interface ResetPasswordParams {
	email: string
}

class AuthClient {
	async signUp(_: SignUpParams): Promise<{ error?: string }> {
		return { error: 'Sign up not implemented' }
	}

	async signInWithOAuth(_: SignInWithOAuthParams): Promise<{ error?: string }> {
		return { error: 'Social authentication not implemented' }
	}

	async signInWithPassword(params: SignInWithPasswordParams): Promise<{ error?: string }> {
		const { email, password } = params

		delete axiosInstance.defaults.headers.common.operatorKey
		Cookies.remove(operatorKey)

		try {
			const response = await axiosInstance.post('sessions', {
				username: email,
				password,
			})

			if (config.fridge.useSessionHeader) {
				const sessionId = response?.headers['x-session-id']
				axiosInstance.defaults.headers.common['x-session-id'] = sessionId
				localStorage.setItem('x-session-id', sessionId)
			}
		} catch (e) {
			return { error: ((e as any).response?.data?.data as string) ?? 'Something went wrong' }
		}

		return {}
	}

	async resetPassword(_: ResetPasswordParams): Promise<{ error?: string }> {
		return { error: 'Password reset not implemented' }
	}

	async updatePassword(_: ResetPasswordParams): Promise<{ error?: string }> {
		return { error: 'Update reset not implemented' }
	}

	async getUser(): Promise<{ data?: User | null; error?: string }> {
		try {
			if (config.fridge.useSessionHeader) {
				const sessionId = localStorage.getItem('x-session-id')
				axiosInstance.defaults.headers.common['x-session-id'] = sessionId
			}
			const response = await axiosInstance.get('sessions')
			const user: User = response.data as User
			user.avatar = user.photo
			return { data: user }
		} catch (e) {
			return { data: null }
		}
	}

	async signOut(): Promise<{ error?: string }> {
		try {
			const response = await axiosInstance.delete('sessions')

			delete axiosInstance.defaults.headers.common.operatorKey
			Cookies.remove(operatorKey)

			// Remove hubspot token and tokenExpiry from localStorage
			localStorage.removeItem('X-Hubspot-Token')
			localStorage.removeItem('X-Hubspot-Token-Expiry')

			return {}
		} catch (e) {
			return { error: 'Something went wrong' }
		}
	}
}

export const authClient = new AuthClient()
